import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { NormeSpaOrdinarioFormValidators } from 'app/features/revisione53/core/forms/amm-controllo/norme-spa-ordinario/validators/norme-spa-ordinario.form-validators';
import { MyVerbale } from '../../../models/interfaces/revisione.interface';
import { NormeSpaOrdinarioWarningService } from './warnings/norme-spa-ordinario.warnings';
import {
    buildAmministratoreUnicoFormArray,
    buildConsiglioAmministrazioneFormArray,
    buildLiquidatoriFormArray,
    buildOrganoControlloFormArray,
    buildRevisoreFormArray,
    clearNormeFormArrays,
} from '../common/helpers/norme-form-groups.helper';
import { normalizeArraysNorme } from '../common/helpers/norme-normalize.helper';
import { autoSetTipoAmministrazioneFromRecordCount, controlloCheckOrgControllo } from '../common/helpers/norme-store-update.helper';
import { CkNormeValuesEnum } from '../common/consts/ckNormeValues.enum';
import { cloneDeep } from 'lodash-es';
import { NormeSpaOrdinarioFormListeners } from './listeners/norme-spa-ordinario.form-listeners';
import { BaseRevisioneFormService } from '../../../abstracts/base-revisione-form.service';
import { formPaths } from '@shared/constants/form-paths.const';
import { getControl } from '@shared/helpers/form.helper';

@Injectable()
export class NormeSpaOrdinarioFormService extends BaseRevisioneFormService<NormeSpaOrdinarioWarningService> {
    constructor(protected warningService: NormeSpaOrdinarioWarningService) {
        super(warningService);
    }

    override initListener(): void {
        new NormeSpaOrdinarioFormListeners(this._form, this.store).setListeners();
    }

    override get dependencyPaths(): Set<string> {
        return new Set([
            // Stato pratica
            'my_StatoVerbale',
            'my_StatoAccertamento',
            'my_IdAssegnazione',

            // Global fields
            'my_GlobalFields.my_StampaMinistero_ChkSRL',
            'my_GlobalFields.my_StampaMinistero_ChkSPO',
            'my_GlobalFields.my_StampaMinistero_ChkSPD',
            'my_GlobalFields.my_StampaMinistero_ChkSPM',

            // Dati identificativi
            `${formPaths.datiIdentificativi}.my_DenominazEnte`,

            // Bilancio
            `${formPaths.bilancio}.my_CapSocSottoscrSecondoEs`,
            `${formPaths.bilancio}.my_CkEnteControllaSocObbRevCont`,
            `${formPaths.bilancio}.my_CkRedazBilSociale`,
            `${formPaths.bilancio}.my_CkRedazBilConsol`,

            // Dati bilancio
            `${formPaths.datiBilancio}.my_BilAnno1`,
            `${formPaths.datiBilancio}.my_BilAnno2`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Attivo.my_StPaAtTOT2`,
            `${formPaths.datiBilancio}.my_StatoPatrimoniale__Attivo.my_StPaAtTOT1`,
            `${formPaths.datiBilancio}.my_ContoEconomico.my_CoEcA12`,

            // Raggiungimento scopo sociale
            `${formPaths.scopoSociale}.my_RicaviVendAnno1Dep`,
            `${formPaths.scopoSociale}.my_RicaviVendDep`,

            // Effettività base sociale
            `${formPaths.baseSociale}.my_SociFisCoopData`,
            `${formPaths.baseSociale}.my_SociGiuCoopData`,
            `${formPaths.baseSociale}.my_SociSpecData`,
            `${formPaths.baseSociale}.my_UnitaSociUtil`,
            `${formPaths.baseSociale}.my_UnitaNonSociUtil`,
            `${formPaths.baseSociale}.my_ULASecondo`,
            `${formPaths.baseSociale}.my_ULAPrimo`,

            // Conclusioni
            `${formPaths.conclusioni}.my_DataCompilaz`,
            `${formPaths.conclusioni}.my_CkProposte`,
        ]);
    }

    protected override initForm(): FormGroup {
        return this.fb.group(
            {
                my_StatoVerbale: '',
                my_StatoAccertamento: '',
                my_DBPresente: '',
                my_IdAssegnazione: '',
                my_GlobalFields: this.fb.group({
                    my_StampaMinistero_ChkSRL: '',
                    my_StampaMinistero_ChkSPO: '',
                    my_StampaMinistero_ChkSPD: '',
                    my_StampaMinistero_ChkSPM: '',
                }),
                my_SezioneI__Rilevazione: this.fb.group({
                    my_DatiIdentificativi: this.fb.group({
                        my_DenominazEnte: '',
                    }),

                    my_Bilancio: this.fb.group({
                        my_CapSocSottoscrSecondoEs: '',
                        my_CkEnteControllaSocObbRevCont: '',
                        my_CkRedazBilSociale: '',
                        my_CkRedazBilConsol: '',
                    }),

                    my_DatiBilancio: this.fb.group({
                        my_BilAnno1: '',
                        my_BilAnno2: '',
                        my_StatoPatrimoniale__Attivo: this.fb.group({
                            my_StPaAtTOT2: '',
                            my_StPaAtTOT1: '',
                        }),

                        my_ContoEconomico: this.fb.group({
                            my_CoEcA12: '',
                        }),
                    }),

                    my_RaggScopoSociale: this.fb.group({
                        my_RicaviVendAnno1Dep: '',
                        my_RicaviVendDep: '',
                    }),

                    my_EffettivitaBaseSociale: this.fb.group({
                        my_SociFisCoopData: '',
                        my_SociGiuCoopData: '',
                        my_SociSpecData: '',
                        my_UnitaSociUtil: '',
                        my_UnitaNonSociUtil: '',
                        my_ULASecondo: '',
                        my_ULAPrimo: '',
                    }),

                    my_Conclusioni: this.fb.group({
                        my_DataCompilaz: '',
                        my_CkProposte: '',
                    }),

                    my_AmministrazioneControllo: this.fb.group({
                        my_CkNorme: '',
                        my_CkLiquidatoriRelazionano: '',
                        my_CriteriRelazioneLiquidatori: '',
                        my_CkSindaciRiunioniLiquidatori: '',
                        my_CkRegistroDelibLiquidatori: '',
                        my_ControlloRevLegali: '',
                        my_ControlloSoci: '',
                        my_CkScadenzaOrgAmm: '',
                        my_DataAssembleaLiquidazVolontaria: '',
                        my_NoteAttivitaLiquidatori: '',
                        my_CkOrgSocietCorrCostit: '',
                        my_CkOrgSocietCorrFunz: '',
                        my_NoteEdOsservazioni: '',
                        my_ControlloRevisoreContabileSocio: '',
                        my_CalcObbOrganoContr: '',
                        my_NumLiquidatori: '',
                        my_NormeSpaOrdinario: this.fb.group({
                            my_lblSpoConsAmm: '',
                            my_lblContrContab: '',
                            my_lblSpoAmmUnico: '',
                            my_lblSpoLiquidatori: '',
                            my_CkSpoGratuitaCariche: '',
                            my_CkSpoCoop9soci: '',
                            my_CkSpoAssembAppBil: '',
                            my_CkSpoAssembNominAmmin: '',
                            my_CkSpoAssembNominSind: '',
                            my_CkSpoAssembNominOrgano: '',
                            my_CkSpoAssembDetCompAmmin: '',
                            my_CkSpoAssembDetCompSindaci: '',
                            my_SpoNoteFunzAssemb: '',
                            my_CkSpoTipoAmministrazione: '',
                            my_SpoScadenzaAmministratori: '',
                            my_CkSpoMaggAmminSoci: '',
                            my_CkSpoNominComitEsec: '',
                            my_SpoAttribComitEsec: '',
                            my_CkSpoAmminRelazionano: '',
                            my_CkSpoSindaciConsAmmin: '',
                            my_CkSpoCompStatoInConsAmmin: '',
                            my_CkSpoCompElettiStrFinan: '',
                            my_CkSpoCompElettiLimite: '',
                            my_SpoNoteComposConsAmmin: '',
                            my_CkSpoRegistroDelib: '',
                            my_SpoNoteAmminUnico: '',

                            my_gruppo3: this.fb.group({
                                my_CKSPOAmmUniRelCarMutua: '',
                            }),

                            my_CkSpoCompStatoInOrgContr: '',
                            my_CkSpoCompOrgContrEletti: '',
                            my_CkSpoRispettoLimite: '',
                            my_CkSpoSindaciRevisori: '',
                            my_CkSpoSindaciSceltiCoerenza: '',
                            my_CkSpoSindaciRelazionano: '',
                            my_SpoNoteComposCollSind: '',
                            my_CkSpoAffidamContrCont: '',
                            my_SpoNoteComposContrContab: '',
                        }),

                        my_AllConsiglioAmministrazione: this.fb.group({
                            my_AllElemConsiglioAmministrazione: this.fb.array([]),
                        }),

                        my_AllAmminUnico: this.fb.array([]),

                        my_AllLiquidatori: this.fb.group({
                            my_AllElemLiquidatori: this.fb.array([]),
                        }),

                        my_SocRevisioneControlloContabile: this.fb.group({
                            my_Id_SocPrecaricata: '',
                            my_RagSocSocRevisione: '',
                            my_DataAffIncarico: '',
                        }),

                        my_AllRevisoriControlloContabile: this.fb.group({
                            my_AllElemRevisoriControlloContabile: this.fb.array([]),
                        }),

                        my_AllCollegioSindacale: this.fb.group({
                            my_AllElemCollegioSindacale: this.fb.array([]),
                        }),
                    }),
                }),
            },
            {
                updateOn: 'change',
                validators: NormeSpaOrdinarioFormValidators,
            }
        );
    }

    protected override beforePopulateForm(myVerbale: MyVerbale): void {
        const v = normalizeArraysNorme(cloneDeep(myVerbale));
        this.patchAll(v);
    }

    private lastCheckVerbaleId: string | null = null;
    private lastCheckMemberCount: number | null = null;

    protected override afterPopulateForm(myVerbale: MyVerbale): void {
        autoSetTipoAmministrazioneFromRecordCount(this._form, this.store, myVerbale, 'spo');
        // REM-42: il VBS ricalcolava i contatori my_ControlloRevLegali/my_ControlloSoci dell'organo
        // di controllo in XDocument_OnLoad. I listener NCR reagiscono solo al cambio membri, quindi
        // il primo calcolo va forzato al load. Si ripete anche a parità di verbale se nel frattempo
        // sono arrivati i membri (caricamento asincrono): senza il controllo sul conteggio, un primo
        // giro con array ancora vuoto blocca il ricalcolo per il resto della sessione.
        const idAssegnazione = myVerbale?.my_IdAssegnazione ?? null;
        if (myVerbale?.my_SezioneI__Rilevazione?.my_AmministrazioneControllo?.my_CkNorme !== CkNormeValuesEnum.normeSpaOrdinario) return;
        const componentiCollegioSindacale = getControl(this._form, `${formPaths.ammControllo}.my_AllCollegioSindacale.my_AllElemCollegioSindacale`)?.value ?? [];
        const memberCount = componentiCollegioSindacale.length;
        if (this.lastCheckVerbaleId === idAssegnazione && this.lastCheckMemberCount === memberCount) return;
        this.lastCheckVerbaleId = idAssegnazione;
        this.lastCheckMemberCount = memberCount;
        controlloCheckOrgControllo(this._form, this.store, componentiCollegioSindacale);
    }

    private patchAll(my_Verbale: MyVerbale): void {
        if (my_Verbale?.my_SezioneI__Rilevazione?.my_AmministrazioneControllo?.my_CkNorme === CkNormeValuesEnum.normeSpaOrdinario) {
            buildOrganoControlloFormArray(this.fb, my_Verbale, this._form);
            buildLiquidatoriFormArray(this.fb, my_Verbale, this._form);
            buildConsiglioAmministrazioneFormArray(this.fb, my_Verbale, this._form);
            buildAmministratoreUnicoFormArray(this.fb, my_Verbale, this._form);
            buildRevisoreFormArray(this.fb, my_Verbale, this._form);
        } else {
            // Verbale di norma diversa: svuota gli array per evitare residui dal verbale precedente.
            clearNormeFormArrays(this._form);
        }
    }
}
